import { useDefaultDbActions, useDefaultModuleActions } from "../../common"
import { fileSubPath, useFileActions } from "../../../features/files"
import { DynamoSystemApi } from "../../../services"
import { TableName } from "../../../tables"
import { define } from "../../../utils/typeUtils"
import { useSystemState, useSystemStateActions } from "../state/hooks"
import { Company, Request, User } from "../state/types"
import { dedupe, firstItem } from "../../../utils/listUtils"
import { O } from "../../../features/ui"

export const useSystemActions = () => {
    const systemState = useSystemState()
    const stateCompany = systemState.company
    const stateCompanyId = stateCompany?.id

    const defaultDbActions = useDefaultDbActions()
    const defaultModuleActions = useDefaultModuleActions()
    const { setCompany, setUser, updateCompanyUsers } = useSystemStateActions()
    const { saveFile, removeFile } = useFileActions()

    return (paramCompanyId?: string) => {
        const companyId = paramCompanyId || stateCompanyId
        const { getAll, get, getByIds, save, remove } = defaultDbActions(DynamoSystemApi)

        const fetchAllCompanies = async () => {
            return getAll(TableName.COMPANIES) as Promise<Company[]>
        }

        const fetchCompanyById = async (id: string) => {
            return get(TableName.COMPANIES, id) as Promise<O<Company>>
        }
        
        const fetchCompanyByCode = async (code: string, saveState = true) => {
            const companies = await getAll(TableName.COMPANIES) as Company[]
            const company = companies.find(company => company.code === code)
            saveState && company && setCompany(company)
            return company
        }

        const saveCompany = async (
            company: Company,
            logoFile?: File,
            preserveLogo = false
        ) => {
            const updatedCompany = {
                ...company
            }
    
            if (logoFile) {
                const fileUrl = await saveFile(fileSubPath(TableName.COMPANIES, company.id, 'logo'), logoFile, true)
                updatedCompany.logoUrl = fileUrl
            } else if (stateCompany?.logoUrl && !preserveLogo) {
                await removeFile(stateCompany.logoUrl, true)
                delete updatedCompany.logoUrl
            }
    
            await save(TableName.COMPANIES, [updatedCompany])
            setCompany(updatedCompany)
        }

        const fetchUserById = async (id: string) => {
            const user = await get(TableName.USERS, id) as O<User>
            if (user) {
                setUser(user)
                return user
            }
        }

        const fetchUserByEmail = async (email: string) => {
            const users = await fetchUsersByEmail(email)
            return firstItem(users)
        }

        const fetchUsersByEmail = async (email: string) => {
            const users = await getAll(TableName.USERS) as User[]
            return users.filter(user => user.email === email)
        }

        // The company users are only loaded for managers (the ones with requests): screens that show who did
        // something load the missing ones, so they show the user name instead of its id
        const fetchCompanyUsers = async (ids: string[]) => {
            const missingIds = dedupe(ids).filter(id => !systemState.companyUsers.some(user => user.id === id))
            if (missingIds.length === 0) return
            const users = await getByIds(TableName.USERS, missingIds) as User[]
            updateCompanyUsers(users)
        }

        const saveUser = async (user: User, saveState = true) => {
            await save(TableName.USERS, [user])
            saveState && setUser(user)
            return user
        }

        const removeUser = (id: string) => {
            return remove(TableName.USERS, [id])
        }

        const saveRequest = async (
            request: Request,
            saveState = true
        ) => {
            if (saveState) {
                const tenantModuleActions = defaultModuleActions(DynamoSystemApi, define(companyId))
                const savedRequest = await tenantModuleActions.save(TableName.REQUESTS, request) as Request
                return savedRequest
            } else {
                const tenantDbActions = defaultDbActions(DynamoSystemApi, define(companyId))
                const savedRequests = await tenantDbActions.save(TableName.REQUESTS, [request]) as Request[]
                return define(firstItem(savedRequests))
            }
        }

        const removeRequest = (id: string) => {
            const { remove } = defaultModuleActions(DynamoSystemApi, define(companyId))
            return remove(TableName.REQUESTS, id)
        }

        return {
            fetchAllCompanies,
            fetchCompanyById,
            fetchCompanyByCode,
            saveCompany,
            fetchUserById,
            fetchUserByEmail,
            fetchUsersByEmail,
            fetchCompanyUsers,
            saveUser,
            removeUser,
            saveRequest,
            removeRequest
        }
    }
}
